import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner, Alert, Badge, ProgressBar, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'sonner';
import {
    IconFileDownload,
    IconUpload,
    IconFileSpreadsheet,
    IconX,
    IconInfoCircle,
    IconArrowLeft,
    IconCheck,
    IconAlertTriangle,
    IconBolt,
} from '@tabler/icons-react';
import { useDownloadRatesTemplate } from '../../../services/rate-import/hooks/useDownloadRatesTemplate';
import { useUploadRates } from '../../../services/rate-import/hooks/useUploadRates';
import { useConfirmRateImport } from '../../../services/rate-import/hooks/useConfirmRateImport';
import { useRateImportStatus } from '../../../services/rate-import/hooks/useRateImportStatus';
import type { UploadResponse } from '../../../services/rate-import/response/RateImportResponse';

interface ImportRatesDialogProps {
    show: boolean;
    onClose: () => void;
    propertyId: string;
    onImported?: () => void;
    /**
     * Dipanggil saat user menutup modal ketika import sedang berjalan.
     * Menyerahkan progress ke floating widget (background) tanpa membatalkan import.
     */
    onBackground?: (importId: string) => void;
}

type Stage = 'setup' | 'preview' | 'processing' | 'done' | 'failed';

// Format harga ringkas (id-ID) tanpa desimal.
const formatPrice = (value: number): string => {
    try {
        return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(value);
    } catch {
        return String(value);
    }
};

const ImportRatesDialog: React.FC<ImportRatesDialogProps> = ({
    show,
    onClose,
    propertyId,
    onImported,
    onBackground,
}) => {
    const [stage, setStage] = useState<Stage>('setup');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadResult, setUploadResult] = useState<UploadResponse['data'] | null>(null);
    const [importId, setImportId] = useState<string | null>(null);

    const { mutate: downloadTemplate, isPending: isDownloading } = useDownloadRatesTemplate();
    const { mutate: uploadRates, isPending: isUploading } = useUploadRates();
    const { mutate: confirmImport, isPending: isConfirming } = useConfirmRateImport();

    // Polling status hanya saat stage 'processing'.
    const { data: statusResponse } = useRateImportStatus(importId, {
        enabled: stage === 'processing' && !!importId,
    });

    const status = statusResponse?.data;

    // Transisi stage berdasarkan hasil polling status.
    useEffect(() => {
        if (stage !== 'processing' || !status) return;
        if (status.status === 'completed') {
            setStage('done');
            toast.success('Rates imported successfully.');
            onImported?.();
        } else if (status.status === 'failed') {
            setStage('failed');
            toast.error(status.error_message || 'Rate import failed.');
        }
    }, [stage, status, onImported]);

    const resetState = useCallback(() => {
        setStage('setup');
        setSelectedFile(null);
        setIsDragOver(false);
        setUploadResult(null);
        setImportId(null);
    }, []);

    const handleClose = useCallback(() => {
        // Jika import sedang berjalan, serahkan ke floating progress widget
        // (background) daripada membatalkan. Import tetap jalan di server.
        if (stage === 'processing' && importId) {
            onBackground?.(importId);
        }
        resetState();
        onClose();
    }, [resetState, onClose, stage, importId, onBackground]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
        // Reset value agar file yang sama bisa dipilih lagi.
        event.target.value = '';
    };

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            setSelectedFile(event.dataTransfer.files[0]);
            event.dataTransfer.clearData();
        }
    }, []);

    const handleDownloadTemplate = () => {
        downloadTemplate(propertyId);
    };

    const handleUpload = () => {
        if (!selectedFile) return;
        uploadRates(
            { propertyId, file: selectedFile },
            {
                onSuccess: (res) => {
                    setUploadResult(res.data);
                    setImportId(res.data.import_id);
                    setStage('preview');
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message || 'Failed to upload file.'
                    );
                },
            }
        );
    };

    const handleBackToSetup = () => {
        setSelectedFile(null);
        setUploadResult(null);
        setImportId(null);
        setStage('setup');
    };

    const handleConfirm = () => {
        if (!importId) return;
        confirmImport(importId, {
            onSuccess: () => {
                setStage('processing');
            },
            onError: (error: any) => {
                toast.error(
                    error?.response?.data?.message || 'Failed to start import.'
                );
            },
        });
    };

    // Kolom tanggal terurut (union semua tanggal dari semua rate plan).
    const sortedDates = useMemo(() => {
        if (!uploadResult) return [];
        const dateSet = new Set<string>();
        Object.values(uploadResult.preview).forEach((row) => {
            Object.keys(row).forEach((d) => dateSet.add(d));
        });
        return Array.from(dateSet).sort();
    }, [uploadResult]);

    const ratePlanIds = useMemo(
        () => (uploadResult ? Object.keys(uploadResult.preview) : []),
        [uploadResult]
    );

    // Tentukan apakah rate plan child berdasarkan cell pertama yang tersedia.
    const isRatePlanChild = useCallback(
        (ratePlanId: string): boolean => {
            if (!uploadResult) return false;
            const cells = Object.values(uploadResult.preview[ratePlanId] || {});
            return cells.length > 0 ? cells[0].is_child : false;
        },
        [uploadResult]
    );

    const progressPct = useMemo(() => {
        if (!status || status.total <= 0) return 0;
        return Math.min(100, Math.round((status.processed / status.total) * 100));
    }, [status]);

    const renderSetup = () => (
        <>
            {/* Download Template */}
            <div className="mb-4">
                <h6 className="fw-bold">Download Template</h6>
                <p className="text-muted small mb-2">
                    The template contains a Rate Plan (parent) dropdown, Start Date, End Date, and Price.
                </p>
                <Button
                    variant="outline-primary"
                    onClick={handleDownloadTemplate}
                    disabled={isDownloading}
                >
                    {isDownloading ? (
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                    ) : (
                        <IconFileDownload size={16} className="me-2" />
                    )}
                    Download Template
                </Button>
            </div>

            <hr />

            {/* Upload File */}
            <div className="mb-4">
                <h6 className="fw-bold">Upload File</h6>
                <p className="text-muted small mb-2">
                    Once the template is filled in, upload the file here to see a preview.
                </p>
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`p-4 border rounded-3 text-center ${
                        isDragOver ? 'border-primary bg-light' : 'border-dashed'
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => document.getElementById('rate-import-file-input')?.click()}
                >
                    <input
                        type="file"
                        id="rate-import-file-input"
                        className="d-none"
                        onChange={handleFileChange}
                        accept=".xlsx, .xls, .csv"
                    />
                    <IconUpload size={48} className="text-muted mb-3" />
                    <p className="mb-1">
                        {isDragOver
                            ? 'Drop the file here!'
                            : 'Drag & drop your file here, or click to browse.'}
                    </p>
                    <small className="text-muted">Supports: .xlsx, .xls, .csv</small>
                </div>

                {selectedFile && (
                    <Alert variant="success" className="d-flex align-items-center mt-3 p-2 mb-0">
                        <IconFileSpreadsheet size={20} className="me-2" />
                        <span className="fw-bold flex-grow-1 text-truncate">
                            {selectedFile.name}
                        </span>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                            onClick={() => setSelectedFile(null)}
                            aria-label="Remove file"
                        >
                            <IconX size={16} />
                        </Button>
                    </Alert>
                )}
            </div>

            <hr />

            {/* Information */}
            <div>
                <h6 className="fw-bold d-flex align-items-center">
                    <IconInfoCircle size={16} className="me-2" />
                    Information
                </h6>
                <ol className="text-muted small mb-0 ps-3">
                    <li>Download the template.</li>
                    <li>Choose a Rate Plan &amp; fill in the dates (Start/End Date) + Price.</li>
                    <li>Upload the completed file.</li>
                    <li>Review the price preview calendar.</li>
                    <li>Click Import Rates Now.</li>
                </ol>
            </div>
        </>
    );

    const renderPreview = () => {
        if (!uploadResult) return null;
        const {
            dp_active,
            errors,
            summary,
            preview,
            rate_plan_names,
            room_type_names,
            plan_room_type,
            list,
        } = uploadResult;
        const planName = (id: string): string => rate_plan_names?.[id] ?? id;
        const roomTypeName = (id: string): string => room_type_names?.[id] ?? id;
        const roomTypeNameForPlan = (rpId: string): string => {
            const rtId = plan_room_type?.[rpId];
            return rtId ? roomTypeName(rtId) : '—';
        };

        return (
            <>
                {dp_active && (
                    <Alert variant="warning" className="d-flex align-items-start p-2">
                        <IconBolt size={18} className="me-2 mt-1 flex-shrink-0" />
                        <div className="small">
                            <strong>Dynamic Pricing is active</strong> — the final price sent to
                            Channex may differ. In the Calendar tab, the estimated adjusted price
                            is labeled <strong>DPM</strong> (DPM = the estimated price
                            adjustment).
                        </div>
                    </Alert>
                )}

                <div className="d-flex flex-wrap gap-2 mb-3 small text-muted">
                    <span>
                        <strong>{summary.rate_plan_count}</strong> rate plan
                    </span>
                    <span>&middot;</span>
                    <span>
                        {summary.date_from} &rarr; {summary.date_to}
                    </span>
                    <span>&middot;</span>
                    <span>
                        <strong>{summary.total_rows}</strong> rows
                    </span>
                    {summary.error_count > 0 && (
                        <>
                            <span>&middot;</span>
                            <span className="text-danger">
                                <strong>{summary.error_count}</strong> skipped
                            </span>
                        </>
                    )}
                </div>

                {errors.length > 0 && (
                    <Alert variant="danger" className="p-2">
                        <div className="d-flex align-items-center mb-1">
                            <IconAlertTriangle size={16} className="me-2" />
                            <strong className="small">
                                {errors.length} row(s) skipped. The remaining rows can still be
                                imported.
                            </strong>
                        </div>
                        <ul className="mb-0 ps-4 small" style={{ maxHeight: 120, overflowY: 'auto' }}>
                            {errors.map((err, idx) => (
                                <li key={idx}>
                                    Row {err.row}: {err.messages.join('; ')}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}

                <Tabs defaultActiveKey="list" className="mb-3" id="rate-import-preview-tabs">
                    {/* ============ TAB LIST (default) ============ */}
                    <Tab eventKey="list" title="List">
                        {list.length === 0 ? (
                            <Alert variant="secondary" className="small mb-0">
                                No valid rows to display.
                            </Alert>
                        ) : (
                            <div className="border rounded-3" style={{ overflowX: 'auto', maxHeight: 420 }}>
                                <table className="table table-sm table-bordered mb-0 align-middle">
                                    <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                        <tr>
                                            <th className="small">Room Type</th>
                                            <th className="small">Rate Plan</th>
                                            <th className="small text-nowrap">Start Date</th>
                                            <th className="small text-nowrap">End Date</th>
                                            <th className="small text-end">Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((row, idx) => (
                                            <tr key={idx}>
                                                <td className="small">{roomTypeName(row.room_type_id)}</td>
                                                <td className="small">{planName(row.rate_plan_id)}</td>
                                                <td className="small text-nowrap">{row.start_date}</td>
                                                <td className="small text-nowrap">{row.end_date}</td>
                                                <td className="small text-end fw-semibold">
                                                    {formatPrice(row.price)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Tab>

                    {/* ============ TAB CALENDAR ============ */}
                    <Tab eventKey="calendar" title="Calendar">
                {ratePlanIds.length === 0 ? (
                    <Alert variant="secondary" className="small mb-0">
                        No valid data to display in the preview.
                    </Alert>
                ) : (
                    <div className="border rounded-3" style={{ overflowX: 'auto' }}>
                        <table className="table table-sm table-bordered mb-0 text-center align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th
                                        className="text-start"
                                        style={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            background: 'var(--bs-light, #f8f9fa)',
                                            minWidth: 220,
                                        }}
                                    >
                                        Room Type / Rate Plan
                                    </th>
                                    {sortedDates.map((date) => (
                                        <th
                                            key={date}
                                            className="small text-nowrap"
                                            style={{ minWidth: 90 }}
                                        >
                                            {date}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {ratePlanIds.map((rpId) => {
                                    const child = isRatePlanChild(rpId);
                                    return (
                                        <tr key={rpId}>
                                            <td
                                                className="text-start"
                                                style={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                    background: 'var(--bs-body-bg, #fff)',
                                                }}
                                            >
                                                <div
                                                    className="text-muted text-truncate"
                                                    style={{ maxWidth: 200, fontSize: '0.65rem' }}
                                                    title={roomTypeNameForPlan(rpId)}
                                                >
                                                    {roomTypeNameForPlan(rpId)}
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        className="text-truncate small"
                                                        style={{ maxWidth: 140 }}
                                                        title={planName(rpId)}
                                                    >
                                                        {planName(rpId)}
                                                    </span>
                                                    <Badge
                                                        bg={child ? 'primary' : 'success'}
                                                        className="text-uppercase text-white fw-bold"
                                                        style={{ fontSize: '0.6rem' }}
                                                    >
                                                        {child ? 'child' : 'parent'}
                                                    </Badge>
                                                </div>
                                            </td>
                                            {sortedDates.map((date) => {
                                                const cell = preview[rpId]?.[date];
                                                if (!cell) {
                                                    return (
                                                        <td key={date} className="text-muted">
                                                            &ndash;
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td key={date}>
                                                        <div className="fw-semibold small">
                                                            {formatPrice(cell.base)}
                                                        </div>
                                                        {dp_active && (
                                                            <div
                                                                className="text-muted"
                                                                style={{ fontSize: '0.7rem' }}
                                                            >
                                                                {formatPrice(cell.adjusted)}{' '}
                                                                <span className="fst-italic">
                                                                    DPM
                                                                </span>
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                    </Tab>
                </Tabs>
            </>
        );
    };

    const renderProcessing = () => (
        <div className="text-center py-4">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h6 className="fw-bold">Importing rates…</h6>
            <p className="text-muted small mb-3">
                The process runs on the server. You can wait here, or close this dialog to
                keep it running in the background.
            </p>
            <ProgressBar
                now={progressPct}
                label={`${progressPct}%`}
                animated
                striped
                className="mb-2"
            />
            {status && (
                <div className="small text-muted">
                    {status.processed} / {status.total} processed
                    {status.failed > 0 && (
                        <span className="text-danger"> &middot; {status.failed} failed</span>
                    )}
                </div>
            )}
        </div>
    );

    const renderDone = () => (
        <div className="text-center py-4">
            <div className="text-success mb-3">
                <IconCheck size={56} />
            </div>
            <h6 className="fw-bold">Rates imported successfully</h6>
            {status && (
                <p className="text-muted small mb-0">
                    {status.processed} processed
                    {status.failed > 0 && (
                        <span className="text-danger"> &middot; {status.failed} failed</span>
                    )}
                </p>
            )}
        </div>
    );

    const renderFailed = () => (
        <div className="py-3">
            <Alert variant="danger" className="d-flex align-items-start p-3 mb-0">
                <IconAlertTriangle size={24} className="me-2 flex-shrink-0" />
                <div>
                    <strong>Import failed</strong>
                    <div className="small mt-1">
                        {status?.error_message || 'An error occurred while processing the import.'}
                    </div>
                </div>
            </Alert>
        </div>
    );

    const titleByStage: Record<Stage, string> = {
        setup: 'Import Rates',
        preview: 'Preview & Confirm',
        processing: 'Importing Rates',
        done: 'Import Complete',
        failed: 'Import Failed',
    };

    // Cegah menutup modal hanya saat upload/confirm request masih in-flight.
    // Saat stage 'processing', modal boleh ditutup — import lanjut di background.
    const preventClose = isUploading || isConfirming;

    return (
        <Modal
            show={show}
            onHide={preventClose ? undefined : handleClose}
            centered
            backdrop="static"
            size={stage === 'preview' ? 'xl' : 'lg'}
        >
            <Modal.Header closeButton={!preventClose}>
                <Modal.Title>{titleByStage[stage]}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {stage === 'setup' && renderSetup()}
                {stage === 'preview' && renderPreview()}
                {stage === 'processing' && renderProcessing()}
                {stage === 'done' && renderDone()}
                {stage === 'failed' && renderFailed()}
            </Modal.Body>
            <Modal.Footer>
                {stage === 'setup' && (
                    <>
                        <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleUpload}
                            disabled={!selectedFile || isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Uploading…
                                </>
                            ) : (
                                <>
                                    <IconUpload size={16} className="me-2" />
                                    Upload &amp; Preview
                                </>
                            )}
                        </Button>
                    </>
                )}

                {stage === 'preview' && (
                    <>
                        <Button
                            variant="outline-secondary"
                            onClick={handleBackToSetup}
                            disabled={isConfirming}
                        >
                            <IconArrowLeft size={16} className="me-2" />
                            Back
                        </Button>
                        <Button variant="primary" onClick={handleConfirm} disabled={isConfirming}>
                            {isConfirming ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Starting…
                                </>
                            ) : (
                                <>
                                    <IconCheck size={16} className="me-2" />
                                    Import Rates Now
                                </>
                            )}
                        </Button>
                    </>
                )}

                {stage === 'processing' && (
                    <Button variant="secondary" onClick={handleClose}>
                        Run in background
                    </Button>
                )}

                {stage === 'done' && (
                    <Button variant="primary" onClick={handleClose}>
                        Done
                    </Button>
                )}

                {stage === 'failed' && (
                    <>
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                        <Button variant="outline-primary" onClick={handleBackToSetup}>
                            Try Again
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ImportRatesDialog;
